// Based on primeng overlaypanel: https://github.com/primefaces/primeng/blob/v12.2.3/src/app/components/overlaypanel/overlaypanel.ts

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output, Renderer2, TemplateRef, ViewChild, ViewContainerRef
} from '@angular/core';
import { OverlayService, PrimeNGConfig } from 'primeng/api';
import { ConnectedOverlayScrollHandler } from '../common/connectedOverlayScrollHandler';
import { AvanoDomHandler } from '../common/domhandler';

@Component({
  selector: 'avano-popover',
  templateUrl: './avano-popover.component.html',
  styleUrls: ['./avano-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvanoPopoverComponent implements OnDestroy {
  @ViewChild('contentInjectionAnchor', { read: ViewContainerRef }) vcRef: ViewContainerRef; // TODO: maybe there is a way without this anchor-DIV to inject correctly
  @ViewChild('container', { static: false }) set containerRef(ref: ElementRef<HTMLDivElement>) {
    this.container = ref?.nativeElement;
    this.popoverVisible = !!ref;
  }
  container: HTMLDivElement;

  @Input() style: { [key: string]: string };
  @Input() styleClass: string;
  @Input() childrenSeparators = true;

  @Input() appendTo: HTMLElement | { el: ElementRef } | 'body' = 'body';
  @Input() contentTemplate: TemplateRef<unknown>;
  @Input() dismissHandlerMouseEvent: 'click' | 'mousedown' = 'click'; // should be same event as used for opening. Usually 'click', in some cases you might need 'mousedown'

  @Output() readonly opened: EventEmitter<void> = new EventEmitter();
  @Output() readonly closed: EventEmitter<void> = new EventEmitter();

  private _popoverVisible = false;
  set popoverVisible(isVisible: boolean) {
    if (isVisible !== this._popoverVisible) {
      this._popoverVisible = isVisible;
      if (isVisible) this.doOpen();
      else this.doClose();
      this.cd.markForCheck();
    }
  }
  get popoverVisible(): boolean {
    return this._popoverVisible;
  }

  render = false;

  documentClickListener: any;
  documentResizeListener: any;
  scrollHandler: any;

  target: any;

  constructor(
    public el: ElementRef,
    public renderer: Renderer2,
    public cd: ChangeDetectorRef,
    private zone: NgZone,
    public config: PrimeNGConfig,
    public overlayService: OverlayService
  ) {}

  bindDocumentClickListener() {
    if (!this.documentClickListener) {
      this.zone.runOutsideAngular(() => {
        const documentEvent = AvanoDomHandler.isIOS() ? 'touchstart' : this.dismissHandlerMouseEvent;
        const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

        this.documentClickListener = this.renderer.listen(documentTarget, documentEvent, (event) => {
          const outsideContainer = !this.container.contains(event.target);
          const outsideTarget = (event.target !== this.target) && !this.target.contains(event.target);
          if (outsideContainer && outsideTarget) {
            this.zone.run(() => {
              this.hide();
            });
          }

          this.cd.markForCheck();
        });
      });
    }
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  toggle(event, target?) {
    if (this.popoverVisible) {
      const newTarget = target || event.currentTarget || event.target;
      const hasTargetChanged = this.target != null && this.target !== newTarget;
      this.hide();
      this.cd.markForCheck();

      if (hasTargetChanged) {
        setTimeout(() => {
          this.show(null, newTarget);
        }, 0);
      }
    } else {
      this.show(event, target);
    }
  }

  show(event, target?) {
    this.target = target || event.currentTarget || event.target;
    this.render = true;
    this.cd.markForCheck();
  }

  onOverlayClick(event: MouseEvent) {
    this.overlayService.add({
      originalEvent: event,
      target: this.el.nativeElement
    });

  }

  appendContainer() {
    if (this.appendTo) {
      if (this.appendTo === 'body') document.body.appendChild(this.container);
      else AvanoDomHandler.appendChild(this.container, this.appendTo);
    }
  }

  restoreAppend() {
    if (this.container && this.appendTo) {
      this.el.nativeElement.appendChild(this.container);
    }
  }

  align() {
    AvanoDomHandler.absolutePosition(this.container, this.target);

    const zIndex = AvanoDomHandler.getZIndex(this.target);
    this.container.style.zIndex = zIndex;

    const containerOffset = AvanoDomHandler.getOffset(this.container);
    const targetOffset = AvanoDomHandler.getOffset(this.target);

    const containerBorderWidth = 1;
    const arrowWidth = 20;
    const arrowLeftMin = 0;
    const arrowLeftCenteredToTarget = targetOffset.left - containerOffset.left + this.target.getBoundingClientRect().width / 2 - arrowWidth/2;
    const arrowLeftMax = this.container.getBoundingClientRect().width - arrowWidth - containerBorderWidth*2;

    const arrowLeft = Math.max(Math.min(arrowLeftCenteredToTarget, arrowLeftMax), arrowLeftMin); // between min/max, ideally centeredToTarget
    this.container.style.setProperty('--popoverArrowLeft', `${Math.round(arrowLeft)}px`);

    if (containerOffset.top < targetOffset.top) {
      AvanoDomHandler.addClass(this.container, 'avano-popover-flipped');
    }
  }

  doOpen() {
    this.opened.emit();
    this.appendContainer();
    this.align();
    this.bindDocumentClickListener();
    this.bindDocumentResizeListener();
    this.bindScrollListener();
  }

  doClose() {
    this.onContainerDestroy();
    this.closed.emit();
    this.render = false;
    this.cd.markForCheck();
  }

  hide() {
    this.popoverVisible = false;
    this.cd.markForCheck();
  }

  onWindowResize() {
    this.hide();
  }

  bindDocumentResizeListener() {
    this.documentResizeListener = this.onWindowResize.bind(this);
    window.addEventListener('resize', this.documentResizeListener);
  }

  unbindDocumentResizeListener() {
    if (this.documentResizeListener) {
      window.removeEventListener('resize', this.documentResizeListener);
      this.documentResizeListener = null;
    }
  }

  bindScrollListener() {
    if (!this.scrollHandler) {
      this.scrollHandler = new ConnectedOverlayScrollHandler(this.target, () => {
        if (this.popoverVisible) {
          this.hide();
        }
      });
    }

    this.scrollHandler.bindScrollListener();
  }

  unbindScrollListener() {
    if (this.scrollHandler) {
      this.scrollHandler.unbindScrollListener();
    }
  }

  onContainerDestroy() {
    this.target = null;
    this.unbindDocumentClickListener();
    this.unbindDocumentResizeListener();
    this.unbindScrollListener();
  }

  ngOnDestroy() {
    if (this.scrollHandler) {
      this.scrollHandler.destroy();
      this.scrollHandler = null;
    }
    this.target = null;
    if (this.container) {
      this.restoreAppend();
      this.onContainerDestroy();
    }
  }
}
